import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, Users, ShieldCheck, BookOpen } from "lucide-react";
import Navbar from "../../components/Navbar";

const trainingAreas = [
  {
    title: "Food Safety Training",
    desc: "Globally recognized Highfield food safety qualifications for food handlers, supervisors, and managers.",
    icon: ShieldCheck
  },
  {
    title: "Health & Safety Training",
    desc: "Workplace safety training aligned with international compliance and risk management standards.",
    icon: Users
  },
  {
    title: "ISO Awareness Programs",
    desc: "Practical ISO awareness sessions enabling teams to understand and implement management systems.",
    icon: BookOpen
  },
  {
    title: "Professional Skill Development",
    desc: "Competency-based learning programs designed to enhance workforce performance.",
    icon: GraduationCap
  }
];

const HighfieldTrainings = () => {
  return (
    <>
    <Navbar />
    <section className="bg-white py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* ================= INTRO STRIP ================= */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)] mb-4">
            Highfield Trainings
          </h2>

          <div className="w-24 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-6"></div>

          <p className="text-gray-600 text-lg leading-relaxed">
            MBA Solutions delivers internationally recognized Highfield training
            programs designed to enhance skills, ensure compliance, and build
            confident professionals across industries.
          </p>
        </motion.div>

        {/* ================= SPLIT EXPERIENCE ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-28">

          {/* LEFT – VISUAL */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <img
              src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655"
              alt="Professional Training Session"
              className="rounded-3xl shadow-2xl object-cover w-full h-[420px]"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-indigo-500/20 rounded-3xl"></div>
          </motion.div>

          {/* RIGHT – CONTENT */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Internationally Recognized Training Excellence
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6">
              Our Highfield training programs are designed to meet global
              competency standards, combining theory, practical learning,
              and real-world application.
            </p>

            <ul className="space-y-3 text-sm text-gray-700">
              <li>✔ Accredited Highfield training programs</li>
              <li>✔ Qualified & experienced trainers</li>
              <li>✔ Interactive learning methodology</li>
              <li>✔ Certification upon successful completion</li>
            </ul>
          </motion.div>
        </div>

        {/* ================= TRAINING CARDS (DIFFERENT STYLE) ================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-32">
          {trainingAreas.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.04 }}
                className="group relative rounded-2xl p-8 bg-gray-50 border border-gray-100 hover:bg-white hover:shadow-xl transition-all"
              >
                <div className="w-14 h-14 rounded-xl bg-[var(--bg-primary)] text-white flex items-center justify-center mb-5 shadow-md">
                  <Icon size={26} />
                </div>

                <h4 className="font-bold text-lg text-gray-900 mb-3">
                  {item.title}
                </h4>

                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.desc}
                </p>

                <div className="absolute bottom-0 left-0 h-1 w-0 bg-[var(--bg-primary)] group-hover:w-full transition-all duration-300 rounded-full"></div>
              </motion.div>
            );
          })}
        </div>

        {/* ================= HIGHLIGHT BANNER ================= */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative bg-[var(--bg-primary)] rounded-3xl p-14 text-center text-white shadow-2xl"
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Learn. Certify. Lead.
          </h3>

          <p className="max-w-2xl mx-auto opacity-90 leading-relaxed">
            Empower your workforce with globally accepted Highfield
            qualifications and practical training that delivers measurable
            impact.
          </p>
        </motion.div>

      </div>
    </section>
    </>
  );
};

export default HighfieldTrainings;
